/**
 * Generates a deterministic color scheme for user avatars based on email
 */

export interface AvatarColorScheme {
  backgroundColor: string;
}

// Color family definitions with hue ranges
interface ColorFamily {
  name: string;
  hueRange: [number, number]; // [min, max] hue values
  saturationBg: [number, number]; // [min, max] saturation for background
  lightnessBg: [number, number]; // [min, max] lightness for background
  saturationText: [number, number]; // [min, max] saturation for text
  lightnessText: [number, number]; // [min, max] lightness for text
}

const COLOR_FAMILIES: ColorFamily[] = [
  {
    name: "blue",
    hueRange: [200, 215],
    saturationBg: [85, 100],
    lightnessBg: [85, 92],
    saturationText: [95, 100],
    lightnessText: [45, 55],
  },
  {
    name: "teal",
    hueRange: [165, 180],
    saturationBg: [70, 85],
    lightnessBg: [82, 90],
    saturationText: [95, 100],
    lightnessText: [35, 45],
  },
  {
    name: "orange",
    hueRange: [15, 25],
    saturationBg: [85, 100],
    lightnessBg: [85, 92],
    saturationText: [95, 100],
    lightnessText: [55, 65],
  },
  {
    name: "purple",
    hueRange: [245, 265],
    saturationBg: [80, 95],
    lightnessBg: [88, 95],
    saturationText: [90, 100],
    lightnessText: [45, 55],
  },
  {
    name: "yellow",
    hueRange: [35, 48],
    saturationBg: [85, 100],
    lightnessBg: [82, 90],
    saturationText: [95, 100],
    lightnessText: [48, 58],
  },
  {
    name: "brown",
    hueRange: [20, 35],
    saturationBg: [35, 50],
    lightnessBg: [80, 88],
    saturationText: [35, 50],
    lightnessText: [40, 50],
  },
];

function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

function generateValueInRange(hash: number, min: number, max: number): number {
  const range = max - min;
  return min + (hash % (range * 100)) / 100;
}

function hslToHex(h: number, s: number, l: number): string {
  l /= 100;
  const a = (s * Math.min(l, 1 - l)) / 100;
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Generates a deterministic color scheme for an avatar based on email
 */
export function getAvatarColorScheme(email: string): AvatarColorScheme {
  if (!email) {
    // Return default blue scheme if no email provided
    return {
      backgroundColor: "#D6EFFF",
    };
  }

  const hash = hashString(email.toLowerCase().trim());

  const familyIndex = hash % COLOR_FAMILIES.length;
  const family = COLOR_FAMILIES[familyIndex];

  const hueHash = Math.floor(hash / COLOR_FAMILIES.length);
  const hue = generateValueInRange(hueHash, family.hueRange[0], family.hueRange[1]);

  const saturationHash = Math.floor(hash / 1000);
  const lightnessHash = Math.floor(hash / 10000);

  const bgSaturation = generateValueInRange(saturationHash, family.saturationBg[0], family.saturationBg[1]);
  const bgLightness = generateValueInRange(lightnessHash, family.lightnessBg[0], family.lightnessBg[1]);

  return {
    backgroundColor: hslToHex(hue, bgSaturation, bgLightness),
  };
}

export function getInitials(firstName?: string, lastName?: string): string {
  const first = firstName?.trim() || "";
  const last = lastName?.trim() || "";

  if (!first && !last) return "";

  const firstInitial = first.charAt(0).toUpperCase();
  const lastInitial = last.charAt(0).toUpperCase();

  return `${firstInitial}${lastInitial}`;
}
